import { Suspense, lazy, useEffect, useMemo, useReducer, useRef, useState, useCallback } from 'react'
import type { AppConfig, Profile } from './types'
import { loadConfig } from './config'
import { initAnalytics, track } from './analytics'
import { makeReducer, initialState, isProviderBased } from './booking/state'
import type { Action, BookingState } from './booking/state'
import { parsePreselect, loadConfirmation, saveConfirmation, syncHash, clearConfirmation, urlParams } from './route'
import { findAfterHoursProfile, isAfterHoursOpen } from './afterHours'
import { Header } from './components/Header'
import { Crumbs } from './components/Crumbs'
import { Toast } from './components/Toast'
import type { ToastMsg } from './components/Toast'
import { ServiceStep } from './components/ServiceStep'
import { SubProfileStep } from './components/SubProfileStep'
import { PatientTypeStep } from './components/PatientTypeStep'
import { RegionStep } from './components/RegionStep'
import { SlotPicker } from './components/SlotPicker'
import { DetailsForm, DetailsFormFallback } from './components/DetailsFormLazy'
import { Confirmation } from './components/Confirmation'
import { SinglePageFlow } from './components/SinglePageFlow'
// Lazy: only the "rail" design uses the sidebar (and its embedded state-map SVGs).
const BrandRail = lazy(() =>
  import('./components/BrandRail').then((m) => ({ default: m.BrandRail }))
)
// Lazy: the quiz funnel loads only for ?quiz=<id> entries — everyone else pays nothing.
const QuizFlow = lazy(() => import('./quiz/QuizFlow'))

/** Whether this quiz was already finished in this tab (mirrors quiz/persist.ts;
 *  inlined so the check doesn't pull the lazy quiz chunk into the main bundle). */
function quizCompleted(quizId: string): boolean {
  try {
    const raw = sessionStorage.getItem(`tpc:quiz:${quizId}`)
    return raw ? !!(JSON.parse(raw) as { completed?: boolean }).completed : false
  } catch {
    return false
  }
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; error: string }
  | { phase: 'ready'; config: AppConfig }

export function App() {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' })

  useEffect(() => {
    let alive = true
    loadConfig()
      .then((config) => {
        if (!alive) return
        initAnalytics(config.analytics)
        setLoad({ phase: 'ready', config })
      })
      .catch((e: unknown) => {
        if (!alive) return
        setLoad({ phase: 'error', error: e instanceof Error ? e.message : 'Failed to load' })
      })
    return () => {
      alive = false
    }
  }, [])

  if (load.phase === 'loading') {
    return (
      <div className="app">
        <main className="app-main">
          <div className="state-block">
            <div className="spinner" aria-hidden />
            <p>Loading…</p>
          </div>
        </main>
      </div>
    )
  }

  if (load.phase === 'error') {
    return (
      <div className="app">
        <main className="app-main">
          <div className="state-block">
            <p className="error-text">Couldn’t load the booking page.</p>
            <button className="btn btn-primary" onClick={() => location.reload()}>
              Retry
            </button>
          </div>
        </main>
      </div>
    )
  }

  return <BookingWidget config={load.config} />
}

/**
 * The booking flow given a resolved config. The page-loading wrapper is `App`.
 */
function BookingWidget({ config }: { config: AppConfig }) {
  const reducer = useMemo(() => makeReducer(config), [config])
  // On a confirmation-page refresh (§5.1), rehydrate the last booking instead of
  // resetting to the first step.
  const restored = useMemo(() => loadConfirmation(), [])
  const [state, dispatch] = useReducer(reducer, restored ?? initialState)
  const [toast, setToast] = useState<ToastMsg | null>(null)

  const notify = useCallback((msg: ToastMsg) => setToast(msg), [])

  // Opt-in quiz funnel: only via ?quiz=<id>. Confirmation restore wins, and a
  // quiz already completed in this tab doesn't re-trigger even though the URL
  // param sticks around.
  const quizId = useMemo(() => urlParams().get('quiz'), [])
  const quiz = quizId ? config.quizzes?.find((q) => q.id === quizId) : undefined
  const [quizActive, setQuizActive] = useState(
    () => !!quiz && !restored && !quizCompleted(quizId!)
  )

  // §1.3: we never auto-request geolocation on load. The user opts in via the
  // "locate" control in the location step.

  // §2.4: preselect profile/status/region from URL params (unless we restored
  // or a quiz will issue its own PRESELECT on completion).
  useEffect(() => {
    if (restored || quizActive) return
    const patch = parsePreselect(config)
    if (Object.keys(patch).length) dispatch({ type: 'PRESELECT', patch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // §5.2: reflect the current step in the URL hash, and persist the confirmation
  // so a refresh can restore it.
  useEffect(() => {
    syncHash(state.step)
    if (state.step === 'confirmation' && state.confirmationId) saveConfirmation(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.confirmationId])

  const goHome = useCallback(() => {
    clearConfirmation()
    dispatch({ type: 'RESTART' })
  }, [])

  const singlePage = config.company.flow === 'single_page'

  // §6.1: after-hours "See a Provider Now" banner — only on the first step and
  // only while inside the configured Central-time window.
  const afterHoursProfile = useMemo(() => findAfterHoursProfile(config), [config])

  // Quiz gate: renders instead of the booking flow until the visitor finishes
  // (→ PRESELECT patch drops them into booking) or closes the quiz. Must sit
  // AFTER every hook above so the hook order is stable when it deactivates.
  if (quizActive && quiz) {
    return (
      <div className="app" data-flow="quiz">
        <Header config={config} onHome={goHome} />
        <main className="app-main">
          <div className="hero-card">
            <Suspense
              fallback={
                <div className="state-block">
                  <div className="spinner" aria-hidden />
                  <p>Loading…</p>
                </div>
              }
            >
              <QuizFlow
                config={config}
                quizId={quiz.id}
                onComplete={(patch) => {
                  setQuizActive(false)
                  if (Object.keys(patch).length) dispatch({ type: 'PRESELECT', patch })
                }}
                onExit={() => setQuizActive(false)}
              />
            </Suspense>
          </div>
        </main>
      </div>
    )
  }
  const showAfterHours =
    state.step === 'service' && !!afterHoursProfile && isAfterHoursOpen(afterHoursProfile)

  const afterHoursBanner = showAfterHours && afterHoursProfile && (
    <AfterHoursBanner
      profile={afterHoursProfile}
      onSelect={() => dispatch({ type: 'SELECT_PROFILE', profile: afterHoursProfile })}
    />
  )
  const flowBody = singlePage ? (
    <SinglePageFlow config={config} state={state} dispatch={dispatch} notify={notify} />
  ) : (
    <>
      <Crumbs config={config} state={state} dispatch={dispatch} />
      <StepView config={config} state={state} dispatch={dispatch} notify={notify} />
    </>
  )
  const availLine = state.step !== 'confirmation' && (
    <div className="avail-line">
      Same-day &amp; Saturday availability
    </div>
  )

  // "rail" design system: sticky blue marketing sidebar + the booking flow as a
  // vertical scroller on the right. Same reducer/steps as every other flow — only
  // the chrome differs (the "India" variant).
  if (config.company.design === 'rail') {
    return (
      <div className="app app-rail" data-flow={config.company.flow || 'single_page'}>
        <div className="rail-layout">
          <Suspense fallback={null}>
            <BrandRail config={config} onHome={goHome} />
          </Suspense>
          <main className="rail-main">
            <div className="rail-booking">
              {afterHoursBanner}
              {state.step !== 'confirmation' && (
                <h1 className="rail-title">{config.copy?.bookingTitle || 'Book an Appointment'}</h1>
              )}
              {flowBody}
              {availLine}
            </div>
          </main>
        </div>
        {toast && <Toast msg={toast} onDismiss={() => setToast(null)} />}
      </div>
    )
  }

  return (
    <div className="app" data-flow={config.company.flow || 'wizard'}>
      <Header config={config} onHome={goHome} />
      <main className="app-main">
        {showAfterHours && afterHoursProfile && (
          <AfterHoursBanner
            profile={afterHoursProfile}
            onSelect={() => dispatch({ type: 'SELECT_PROFILE', profile: afterHoursProfile })}
          />
        )}
        <div className="hero-card">
          {config.company.tagline && <CardEyebrow text={config.company.tagline} />}
          {singlePage ? (
            <SinglePageFlow config={config} state={state} dispatch={dispatch} notify={notify} />
          ) : (
            <>
              <Crumbs config={config} state={state} dispatch={dispatch} />
              <StepView config={config} state={state} dispatch={dispatch} notify={notify} />
              {state.step !== 'confirmation' && (
                <div className="avail-line">
                  Same-day &amp; Saturday availability
                </div>
              )}
            </>
          )}
        </div>
      </main>
      {toast && <Toast msg={toast} onDismiss={() => setToast(null)} />}
    </div>
  )
}

/** After-hours "See a Provider Now" banner shown above the booking card (§6.1). */
function AfterHoursBanner({ profile, onSelect }: { profile: Profile; onSelect: () => void }) {
  return (
    <button type="button" className="after-hours-banner" onClick={onSelect}>
      <span className="ah-text">
        <strong>{profile.name}</strong>
        {profile.description && <span className="ah-sub">{profile.description}</span>}
      </span>
      <span className="ah-cta" aria-hidden>
        →
      </span>
    </button>
  )
}

/** Display-font eyebrow above the card; highlights "Primary Care" in brand blue. */
function CardEyebrow({ text }: { text: string }) {
  const accent = 'Primary Care'
  const idx = text.indexOf(accent)
  if (idx === -1) return <div className="card-eyebrow">{text}</div>
  return (
    <div className="card-eyebrow">
      {text.slice(0, idx)}
      <span className="accent">{accent}</span>
      {text.slice(idx + accent.length)}
    </div>
  )
}

function StepView({
  config,
  state,
  dispatch,
  notify
}: {
  config: AppConfig
  state: BookingState
  dispatch: React.Dispatch<Action>
  notify: (m: ToastMsg) => void
}) {
  const back = () => {
    track('select_previous_page')
    dispatch({ type: 'BACK' })
  }

  // Move focus to the new step's heading so keyboard/screen-reader users aren't
  // left on the unmounted control they just clicked. Skips the initial render
  // (don't steal focus on load); Confirmation manages its own focus. The retry
  // covers the lazy details chunk, whose heading may mount a beat later.
  const firstStep = useRef(true)
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false
      return
    }
    if (state.step === 'confirmation') return
    let cancelled = false
    let attempts = 0
    const tryFocus = () => {
      if (cancelled) return
      const el = document.querySelector<HTMLElement>('h1.step-heading')
      if (el) {
        el.setAttribute('tabindex', '-1')
        el.focus({ preventScroll: true })
      } else if (attempts++ < 10) {
        setTimeout(tryFocus, 100)
      }
    }
    tryFocus()
    return () => {
      cancelled = true
    }
  }, [state.step])

  switch (state.step) {
    case 'service':
      return <ServiceStep config={config} dispatch={dispatch} />
    case 'subProfile':
      return <SubProfileStep config={config} state={state} dispatch={dispatch} onBack={back} />
    case 'patientType':
      return <PatientTypeStep config={config} state={state} dispatch={dispatch} onBack={back} />
    case 'region':
      return <RegionStep config={config} dispatch={dispatch} onBack={back} />
    case 'slots':
      return (
        <SlotPicker
          config={config}
          state={state}
          dispatch={dispatch}
          onBack={back}
          providerBased={isProviderBased(config, state.profile)}
        />
      )
    case 'details':
      return (
        <Suspense fallback={<DetailsFormFallback />}>
          <DetailsForm config={config} state={state} dispatch={dispatch} notify={notify} onBack={back} />
        </Suspense>
      )
    case 'confirmation':
      return <Confirmation config={config} state={state} dispatch={dispatch} />
    default:
      return null
  }
}
